import { z } from 'zod';
/**
 * Реестр фоновых задач.
 *
 * Почему он лежит в shared, а не в backend. Очередь — это место, где данные
 * переживают перезапуск процесса: задача попадает в Redis в одной версии кода,
 * а исполняется в другой. Если полезная нагрузка описана только типом
 * TypeScript, то после развёртывания в очереди могут остаться задачи, форма
 * которых новому коду уже не знакома, и воркер упадёт на разборе. Поэтому у
 * каждой задачи есть схема Zod: она проверяется и при постановке, и при
 * исполнении. Несовпадение обнаруживается сразу и с понятным текстом, а не
 * как «cannot read property of undefined» через три звена.
 *
 * Общие для всех задач поля собраны в jobMetaSchema. Главное из них —
 * requestId: он связывает фоновую работу с тем HTTP-запросом, который её
 * породил, поэтому в журнале видно всю цепочку, а не два несвязанных события.
 */
/** Поля, которые добавляются к любой задаче автоматически. */
export const jobMetaSchema = z.object({
    /** Воркспейс, от имени которого выполняется работа. Пусто — системная задача. */
    workspaceId: z.uuid().nullable().default(null),
    /** Кто инициировал. Пусто — задача по расписанию. */
    actorUserId: z.uuid().nullable().default(null),
    /** Идентификатор запроса, из которого задача поставлена. Связывает логи. */
    requestId: z.string().max(64).nullable().default(null),
});
/** Очистка истёкших и отозванных сессий. Ставится по расписанию. */
export const cleanupSessionsPayloadSchema = z.object({
    meta: jobMetaSchema,
    /** Сколько дней хранить уже недействительные сессии, прежде чем удалить. */
    retentionDays: z.number().int().min(0).max(3650),
});
/**
 * Диагностическая задача. Единственный её смысл — на живой системе показать,
 * что цепочка «повтор → исчерпание попыток → мёртвое письмо» действительно
 * работает. Доступна только владельцу и только вне продакшна.
 */
export const diagnosticsPayloadSchema = z.object({
    meta: jobMetaSchema,
    /** ok — завершиться успешно, fail — упасть на каждой попытке. */
    behaviour: z.enum(['ok', 'fail']),
    /** Искусственная задержка, чтобы задачу было видно в состоянии «в работе». */
    delayMs: z.number().int().min(0).max(30_000).default(0),
    /** Текст, который попадёт в причину сбоя — так проще узнать свой прогон. */
    note: z.string().max(200).default('проверка очереди'),
});
/**
 * Обработка входящего сообщения: разбудить агента и дать ему ответить.
 *
 * Задача ставится сразу после сохранения сообщения, а не выполняется
 * в обработчике вебхука. Причина в требовании ChatApp и в здравом смысле:
 * наш адрес должен ответить 200 немедленно, до всякой работы, иначе
 * провайдер посчитает доставку неудачной. Вся работа — обращение к модели,
 * вызовы инструментов, отправка ответа — идёт в очереди.
 *
 * Идентификатор сообщения входит в нагрузку, чтобы задача была
 * идемпотентна по нему: повторная доставка того же события не должна
 * приводить ко второму ответу клиенту.
 */
export const processInboundPayloadSchema = z.object({
    meta: jobMetaSchema,
    conversationId: z.uuid(),
    messageId: z.uuid(),
});
/**
 * Напоминание замолчавшему клиенту.
 *
 * Задача откладывается на паузу из настроек агента и ставится с ключом
 * по диалогу — одно напоминание на диалог, не больше. Ответ клиента
 * снимает её: тогда напоминать не о чем.
 *
 * Номер шага в нагрузке, а не только в базе: по нему видно, какое из
 * напоминаний пришло, и он же делает повтор безвредным — если шаг уже
 * отправлен, обработчик ничего не делает.
 */
export const followUpPayloadSchema = z.object({
    meta: jobMetaSchema,
    conversationId: z.uuid(),
    /** Номер напоминания, начиная с единицы. У последнего шага — ноль. */
    step: z.number().int().min(0).max(5),
    /**
     * Обычный шаг цепочки или последняя попытка.
     *
     * Последняя живёт по своим часам: она отсчитывается от начала
     * разговора, а не от молчания, и уходит один раз — перед тем как
     * закроется суточное окно WhatsApp, после которого написать первым
     * уже нельзя без платного шаблона.
     */
    kind: z.enum(['step', 'last']).default('step'),
});
/**
 * Обновление каталога товаров из внешнего сервиса.
 *
 * Ставится по расписанию, дважды в сутки — так делала и старая админка.
 * Чаще незачем: каталог меняется редко, а каждый запрос — поход в чужую
 * систему.
 *
 * `workspaceId` пустой означает «все воркспейсы, у которых настроен
 * ключ». Это и есть случай расписания; заполненный нужен ручному
 * запуску из админки, когда товар завели только что и ждать полдня
 * не хочется.
 */
export const syncCatalogPayloadSchema = z.object({
    meta: jobMetaSchema,
    workspaceId: z.uuid().nullable().default(null),
});
/**
 * Обновление справочника групп офферов из Робота.
 *
 * Раз в сутки, как велит инструкция их разработчика: «других обращений
 * к Роботу нет — ни чаще, ни по запросу, ни при сообщении клиента».
 * Поиск при входящем идёт только в своей базе.
 *
 * Пустой `workspaceId` означает «все воркспейсы»: справочник у Робота
 * один на установку, доступ к нему задан в окружении, и копия нужна
 * каждому воркспейсу, который принимает заявки. Заполненный нужен
 * ручному запуску.
 */
export const syncOfferGroupsPayloadSchema = z.object({
    meta: jobMetaSchema,
    workspaceId: z.uuid().nullable().default(null),
});
/**
 * Единственная точка правды о составе задач.
 * Добавление задачи — это добавление ключа сюда; всё остальное
 * (типы, проверка, отправка, диспетчеризация) выводится из этой таблицы.
 */
export const JOB_SCHEMAS = {
    'cleanup-sessions': cleanupSessionsPayloadSchema,
    diagnostics: diagnosticsPayloadSchema,
    'process-inbound': processInboundPayloadSchema,
    'follow-up': followUpPayloadSchema,
    'sync-catalog': syncCatalogPayloadSchema,
    'sync-offer-groups': syncOfferGroupsPayloadSchema,
};
export const JOB_NAMES = Object.keys(JOB_SCHEMAS);
export const jobNameSchema = z.enum(JOB_NAMES);
/**
 * Проверка через Object.hasOwn, а не через оператор `in`, и это не
 * придирка к стилю. Оператор `in` видит унаследованные свойства, поэтому
 * строки вроде «toString» или «constructor» он признал бы именем задачи —
 * а обращение JOB_SCHEMAS['toString'] вернуло бы функцию, у которой нет
 * метода разбора. Имя задачи приходит из Redis, то есть извне, и полагаться
 * на то, что таких строк там не окажется, не стоит.
 */
export function isJobName(value) {
    return typeof value === 'string' && Object.hasOwn(JOB_SCHEMAS, value);
}
/**
 * Имя очереди. Пока одна: задач немного, а одна очередь означает один воркер,
 * один набор метрик и одно место, где смотреть. Разделять стоит тогда, когда
 * появятся задачи с разными требованиями к задержке — например, когда
 * медленная выгрузка отчёта начнёт задерживать ответ агента в диалоге.
 */
export const DEFAULT_QUEUE_NAME = 'ai-sales';
/**
 * Политика повторов по умолчанию.
 *
 * Пять попыток с удвоением задержки от двух секунд дают примерно полминуты
 * перекрытия: 2с, 4с, 8с, 16с. Этого хватает, чтобы пережить перезапуск
 * зависимости или короткий сетевой сбой, и мало, чтобы задача не висела
 * часами, если сломано всерьёз. Разброс (jitter) внутри BullMQ не нужен —
 * задачи ставятся в разное время и сами по себе не синхронизированы.
 */
export const DEFAULT_JOB_ATTEMPTS = 5;
export const DEFAULT_JOB_BACKOFF_MS = 2_000;
//# sourceMappingURL=jobs.js.map